import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { WorkflowConfig } from '@kfolks/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { requestCenterApi } from '@/services/request-center-api'
import { hasAnyAdvancedStep } from './advanced-step'
import { NodePalette } from './NodePalette'
import { NodePropertiesPanel } from './NodePropertiesPanel'
import { RawJsonEditor } from './RawJsonEditor'
import { ValidationBadge } from './ValidationBadge'
import { useWorkflowConfigValidation, type WorkflowConfigValidationView } from './useWorkflowConfigValidation'
import { WorkflowBuilderHeader } from './WorkflowBuilderHeader'
import { WorkflowBuilderStateProvider, useWorkflowBuilderState } from './WorkflowBuilderState'
import { WorkflowCanvas, dispatchWorkflowDragEnd } from './WorkflowCanvas'
import { VersionHistoryPanel } from '../shared/version-history/VersionHistoryPanel'

/**
 * Workflow-builder page shell. Tabs in this exact order, per the
 * `request-center-workflow-builder` capability spec:
 *
 *     Visual  |  History  |  Raw JSON
 *
 * No Preview tab (the workflow doesn't render to an end-user — it executes
 * server-side via `WorkflowEngineService`). Default tab on load is
 * **Visual**. The **Raw JSON** tab MUST always be reachable — never
 * disabled, never feature-flagged — regardless of payload validity,
 * presence of advanced/passthrough steps, or canvas render state. Per
 * the workflow-builder capability spec ("Raw JSON cannot be hidden by
 * feature flag or role"), it is the recovery path: if the visual canvas
 * crashes on a malformed payload, the admin must still be able to fix
 * the JSON by hand and save.
 *
 * Per ADR-0012 the canvas authors the linear step-array contract
 * (`WorkflowConfig`), NOT XState. There is no XState representation
 * anywhere in this builder; the canvas in subsequent tasks dispatches
 * over `WorkflowStepConfig`'s discriminated union (`approval` /
 * `execution` / `info_gate`).
 *
 * Phase D wiring: this is the page-level shell. Subsequent tasks land:
 *   - 8.2  NodePalette (three real step types only — no XState "Final
 *          State" / "Provisioning" pseudo-steps)
 *   - 8.3  WorkflowCanvas (`@xyflow/react` step-graph; edges author
 *          `after.next` with success/failure)
 *   - 8.4  NodePropertiesPanel (per-step-type editors mapping exactly
 *          to the persisted shape)
 *   - 8.5  DagValidator (thin wrapper over the shared
 *          `validateWorkflowConfig` from `@kfolks/models`)
 *   - 8.6  ValidationBadge (top-of-canvas summary)
 *   - 8.7  RawJsonEditor (state-bridged version of the form-builder one)
 *   - 8.8  Advanced-passthrough rendering for unsupported step shapes
 *   - 8.9  Save action (canonicalized PUT)
 *   - 9.x  VersionHistoryPanel + diff modal (shared with form builder)
 *
 * The Raw JSON tab is the recovery path — admins always retain
 * direct-JSON editing even when the visual canvas can't render a
 * payload. (The standalone Spec 2 `WorkflowEditorPage` route is gone
 * now that the visual builder is the default; the Raw JSON tab is its
 * functional replacement.)
 */
export function WorkflowBuilderPage() {
	const { id } = useParams<{ id: string }>()
	const [activeTab, setActiveTab] = useState<TabValue>(DEFAULT_TAB)

	// Seed the builder from the persisted workflow_config. Same query the
	// existing Spec 2 JSON editor uses; the cache is usually warm from the
	// parent admin page.
	const list = useQuery({
		queryKey: ['request-types', { admin: true }],
		queryFn: () => requestCenterApi.listRequestTypes({ includeInactive: true, limit: 100 }),
	})
	const persistedType = useMemo(() => list.data?.find((rt) => rt.id === id), [list.data, id])
	const persistedConfig = useMemo<WorkflowConfig | undefined>(() => {
		const raw = persistedType?.workflowConfig as unknown
		// Accept the shipped step-array shape; treat the legacy `{}` /
		// missing case as empty (engine treats `{}` as "no workflow"
		// already, so the builder seeding semantics match the runtime).
		return isWorkflowConfigShape(raw) ? raw : undefined
	}, [persistedType])

	// Gate initial render on the query (same pattern as `FormBuilderPage` —
	// the state provider only seeds on first render; mounting before the
	// query resolved would leave the canvas stuck on `EMPTY_CONFIG` even
	// for types with a real persisted config).
	if (list.isLoading) {
		return (
			<div className="space-y-4">
				<div
					role="status"
					aria-busy="true"
					aria-label="Loading request type"
					className="h-10 animate-pulse rounded-md bg-muted"
				/>
				<div aria-busy="true" className="h-64 animate-pulse rounded-md bg-muted" />
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<WorkflowBuilderStateProvider initialConfig={persistedConfig}>
				<WorkflowBuilderInner
					typeId={id}
					typeName={persistedType?.name}
					activeTab={activeTab}
					setActiveTab={setActiveTab}
				/>
			</WorkflowBuilderStateProvider>
		</div>
	)
}

function WorkflowBuilderInner({
	typeId,
	typeName,
	activeTab,
	setActiveTab,
}: {
	typeId: string | undefined
	typeName: string | undefined
	activeTab: TabValue
	setActiveTab: (v: TabValue) => void
}) {
	// Validation is computed here so both the header (Save gate) and the
	// Visual tab (badge) read the same snapshot. Lives inside the provider
	// so the hook has access to `workflowConfig` from context.
	const { workflowConfig } = useWorkflowBuilderState()
	const validation = useWorkflowConfigValidation(workflowConfig)
	return (
		<>
			<WorkflowBuilderHeader typeId={typeId} typeName={typeName} validation={validation} />
			<Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="w-full">
				<TabsList aria-label="Workflow builder tabs">
					<TabsTrigger value="visual">Visual</TabsTrigger>
					<TabsTrigger value="history">History</TabsTrigger>
					{/* Raw JSON tab — never disabled, never hidden, never feature-flagged. */}
					<TabsTrigger value="raw-json">Raw JSON</TabsTrigger>
				</TabsList>

				<TabsContent value="visual">
					<VisualTab validation={validation} />
				</TabsContent>

				<TabsContent value="history">{typeId ? <VersionHistoryPanel requestTypeId={typeId} /> : null}</TabsContent>

				<TabsContent value="raw-json">
					{/* Recovery path — always reachable, regardless of state on the other tabs.
					    Wired through the shared `WorkflowBuilderState` so JSON edits round-trip
					    with the Visual tab. */}
					<RawJsonEditor />
				</TabsContent>
			</Tabs>
		</>
	)
}

type TabValue = 'visual' | 'history' | 'raw-json'
const DEFAULT_TAB: TabValue = 'visual'

// ─── Placeholder panels (still pending) ──────────────────────────────────────

function VisualTab({ validation }: { validation: WorkflowConfigValidationView }) {
	const { workflowConfig, insertStep, selectStep } = useWorkflowBuilderState()
	const [advancedBannerDismissed, setAdvancedBannerDismissed] = useState(false)
	const showAdvancedBanner = !advancedBannerDismissed && hasAnyAdvancedStep(workflowConfig.steps)
	// PointerSensor with a small activation distance — clicks on the
	// palette's `+` button (and the drag-handle button) need to NOT
	// register as drags; require ~5px of pointer movement before
	// activating drag. Without this, click-vs-drag is ambiguous and
	// drag-from-palette can fail silently.
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
	return (
		<DndContext sensors={sensors} onDragEnd={(event) => dispatchWorkflowDragEnd(event, { insertStep, selectStep })}>
			<div
				role="region"
				aria-label="Workflow visual builder"
				className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr_320px]"
			>
				<aside className="rounded-md border bg-card p-3">
					<h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Step types</h3>
					<NodePalette />
				</aside>
				<section className="space-y-2">
					{showAdvancedBanner ? (
						<div
							role="alert"
							data-testid="advanced-steps-banner"
							className="flex items-start justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400"
						>
							<div>
								<p className="font-medium">This workflow contains advanced steps</p>
								<p className="text-xs">
									Some steps have fields the visual builder doesn't surface (e.g., a non-trivial{' '}
									<span className="font-mono">after.guard</span>). They render read-only on the canvas but round-trip
									byte-equivalently on save. Edit them in the <span className="font-mono">Raw JSON</span> tab.
								</p>
							</div>
							<button
								type="button"
								onClick={() => setAdvancedBannerDismissed(true)}
								aria-label="Dismiss advanced-steps notice"
								className="text-xs underline"
							>
								Dismiss
							</button>
						</div>
					) : null}
					<ValidationBadge validation={validation} />
					<WorkflowCanvas />
				</section>
				<aside className="rounded-md border bg-card p-3">
					<h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Properties</h3>
					<NodePropertiesPanel />
				</aside>
			</div>
		</DndContext>
	)
}

/**
 * Type-narrowing predicate for the persisted workflowConfig — keeps types
 * honest at the JSON-narrowing seam without resorting to `as unknown as
 * WorkflowConfig`. Per the no-double-cast convention.
 */
function isWorkflowConfigShape(raw: unknown): raw is WorkflowConfig {
	if (!raw || typeof raw !== 'object') return false
	const r = raw as { version?: unknown; steps?: unknown }
	return r.version === '1.0' && Array.isArray(r.steps)
}
